import React, { JSX, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";
import color from "../../resource/color";
import Text from "../Text/Text";
import { GiftOutlined, ShopOutlined, SettingFilled } from "@ant-design/icons";
import CollapseMenu from "./CollapseMenu";
import { useEffectOnce } from "react-use";
import { checkPermissionRenderMenu } from "../../utility/func/RedirectByPermission";
import { useRecoilValue } from "recoil";
import { roleAtom } from "../../store/RoleAtom";
import packageJson from "../../../package.json";
import { mockRoles } from "../../utility/StaticPermission";
import { isArray } from "lodash";
import Permission from "../Permission/Permission";
import icon from "../../resource/icon";
import useShopTierPendingNav from "../../hook/useShopTierPendingNav";

interface Props {
  style?: React.CSSProperties;
  lists: {
    path: string;
    name: string;
    title: string;
    permission: string[];
    subMenu: {
      path: string;
      name: string;
      title: string;
      permission: string;
    }[];
  }[];
  isOpenSidebar?: boolean;
}
const MenuSiderStyled = styled.div`
  background-color: white;
  height: 100%;
  padding-top: 48px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
const ListStyled = styled.div<{ isOpen?: boolean; isFocus?: boolean }>`
  ${({ isOpen }) => {
    if (isOpen) {
      return css`
        display: grid;
        align-items: center;
        grid-template-columns: 20px 140px;
      `;
    } else {
      return css`
        display: flex;
        align-items: center;
      `;
    }
  }}
  ${({ isFocus }) => {
    if (isFocus) {
      return css`
        background-color: ${color.secondary};
        color: white;
        border-radius: 8px;
      `;
    } else {
      return css`
        background-color: white;
        color: black;
      `;
    }
  }}
  /* Same box whether focused or not, so the absolutely positioned MenuDot lands in one place. */
  margin-left: ${({ isOpen }) => (isOpen ? "8px" : "16px")};
  margin-right: ${({ isOpen }) => (isOpen ? "8px" : "16px")};
  position: relative;
  padding: 8px;
  margin-bottom: 8px;
  gap: 8px;
  justify-content: center;
  cursor: pointer;
  &:hover {
    color: ${(props) => (props.isFocus ? "white" : color.secondary)};
  }
`;

// Orange attention dot: vertically centred on the right when the sidebar is open,
// on the item's top-right corner when it is collapsed to icons.
const MenuDot = styled.span<{ isOpen?: boolean }>`
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${color.softWarning};
  ${({ isOpen }) =>
    isOpen
      ? css`
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
        `
      : css`
          right: -4px;
          top: -4px;
        `}
`;

export const TextStyled = styled(Text)<{ isFocus?: boolean }>`
  ${({ isFocus }) => {
    if (isFocus) {
      return css`
        color: white;
      `;
    } else {
      return css`
        color: ${color.Text1};
      `;
    }
  }}
  font-family: Helvetica !important;
  &:hover {
    color: ${(props) => (props.isFocus ? "white" : color.secondary)};
  }
`;
const iconStyle = { width: "24px", height: "24px" };

function ShopTierIcon({ color: strokeColor }: { color: string }) {
  return (
    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke={strokeColor} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="6" />
      <polygon points="12,5 12.85,6.85 14.85,7.1 13.35,8.5 13.75,10.5 12,9.5 10.25,10.5 10.65,8.5 9.15,7.1 11.15,6.85" />
      <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
    </svg>
  );
}

const iconsInActive = {
  report: {
    active: <img src={icon.report} style={iconStyle} />,
    inactive: <img src={icon.report_inactive} style={iconStyle} />,
  },
  order: {
    active: <img src={icon.order} style={iconStyle} />,
    inactive: <img src={icon.order_inactive} style={iconStyle} />,
  },
  specialRequest: {
    active: <img src={icon.approve_order} style={iconStyle} />,
    inactive: <img src={icon.approve_order_inactive} style={iconStyle} />,
  },
  promotionSetting: {
    active: <img src={icon.promotion} style={iconStyle} />,
    inactive: <img src={icon.promotion_inactive} style={iconStyle} />,
  },
  detectOrder: {
    active: <img src={icon.detection} style={iconStyle} />,
    inactive: <img src={icon.detection_inactive} style={iconStyle} />,
  },
  productQuota: {
    active: <img src={icon.productQuota_active} style={iconStyle} />,
    inactive: <img src={icon.productQuota_inactive} style={iconStyle} />,
  },
  promotionNoti: (
    <GiftOutlined
      style={{
        fontSize: "20px",
      }}
    />
  ),
  freebies: (
    <GiftOutlined
      style={{
        fontSize: "20px",
      }}
    />
  ),
  discountList: {
    active: <img src={icon.discount} style={iconStyle} />,
    inactive: <img src={icon.discount_inactive} style={iconStyle} />,
  },
  newDiscountCo: {
    active: <img src={icon.discount} style={iconStyle} />,
    inactive: <img src={icon.discount_inactive} style={iconStyle} />,
  },
  priceList: {
    active: <img src={icon.product} style={iconStyle} />,
    inactive: <img src={icon.product_inactive} style={iconStyle} />,
  },
  price: (
    <ShopOutlined
      style={{
        fontSize: "20px",
      }}
    />
  ),
  shopManagement: {
    active: <img src={icon.manage_shop} style={iconStyle} />,
    inactive: <img src={icon.manage_shop_inactive} style={iconStyle} />,
  },
  user: {
    active: <img src={icon.user} style={iconStyle} />,
    inactive: <img src={icon.user_inactive} style={iconStyle} />,
  },
  news: {
    active: <img src={icon.news} style={iconStyle} />,
    inactive: <img src={icon.news_inactive} style={iconStyle} />,
  },
  productshop: {
    active: <img src={icon.product} style={iconStyle} />,
    inactive: <img src={icon.product_inactive} style={iconStyle} />,
  },
  generalSettings: {
    active: <img src={icon.setting} style={iconStyle} />,
    inactive: <img src={icon.setting_inactive} style={iconStyle} />,
  },
  oneFinity: (
    <SettingFilled
      style={{
        fontSize: "20px",
      }}
    />
  ),
  receiveGoods: {
    active: <img src={icon.receive_product} style={iconStyle} />,
    inactive: <img src={icon.receive_product_inactive} style={iconStyle} />,
  },
  crm: {
    active: <img src={icon.crm} style={iconStyle} />,
    inactive: <img src={icon.crm_inactive} style={iconStyle} />,
  },
  DOReport: {
    active: <img src={icon.deliveryDoc} style={iconStyle} />,
    inactive: <img src={icon.deliveryDocInactive} style={iconStyle} />,
  },
  ShopTier: {
    active: <ShopTierIcon color="#fff" />,
    inactive: <ShopTierIcon color="#464E5F" />,
  },
};
const renderIcon = (name: string, isActive: boolean) => {
  const icon = iconsInActive[name];
  if (icon && typeof icon === "object" && icon.active && icon.inactive) {
    return isActive ? icon.active : icon.inactive;
  }
  if (React.isValidElement(icon)) {
    return icon;
  } else if (typeof icon === "string" || icon instanceof HTMLImageElement) {
    return <img src={icon as string} alt={name} style={{ width: "24px", height: "24px" }} />;
  }
  return null;
};

function MenuSider({ style, lists = [], isOpenSidebar = false }: Props): JSX.Element {
  const navigate = useNavigate();
  const [current, setCurrent] = useState({
    path: "",
    subPath: "",
  });
  const roleData = useRecoilValue(roleAtom);
  const showShopTierDot = useShopTierPendingNav();
  const hasDot = (name: string) => name === "ShopTier" && showShopTierDot;

  useEffectOnce(() => {
    const pathName = window.location.pathname;
    const pathNameSplit = pathName.split("/").filter((item) => item !== "");

    const currentPath = lists.find((item) => item.path === `/${pathNameSplit[0]}`);
    if (currentPath) {
      const isHaveSubPath = currentPath.subMenu.find((el) => el.path === `/${pathNameSplit[1]}`);

      setCurrent({
        path: currentPath.name,
        subPath: isHaveSubPath ? isHaveSubPath.name : "",
      });
    }
  });

  const onClickList = (name: string) => {
    setCurrent({
      path: name,
      subPath: "",
    });
  };
  const newRoles = mockRoles.map((el) => {
    return {
      ...el,
      permission: isArray(el.menu) ? [] : Object.keys(el.menu),
    };
  });

  const permissionList: string[] = [];
  (roleData?.menus || []).forEach((el: { permission: any; menuName: string }) => {
    if (isArray(el?.permission) && el?.permission.length > 0) {
      permissionList.push(el.menuName);
    }
    if (el?.permission && !isArray(el.permission) && Object.keys(el.permission)?.length > 0) {
      permissionList.push(el.menuName);
      Object.keys(el.permission).forEach((key) => {
        if (el.permission[key].length > 0) {
          permissionList.push(key);
        }
      });
    }
  });

  return (
    <MenuSiderStyled style={style}>
      <div>
        {lists.map((list, idx) => {
          const isPremiss = checkPermissionRenderMenu({
            menus: newRoles,
            permission: permissionList,
          });
          const mapMenuPermission = permissionList.find((x) => x === list.permission[0]);
          if (!isPremiss) return null;
          if (mapMenuPermission) {
            if (list.subMenu.length < 1) {
              return (
                <ListStyled
                  key={idx}
                  isOpen={isOpenSidebar}
                  isFocus={current.path === list.name}
                  onClick={() => {
                    onClickList(list.name);
                    navigate(list.path);
                  }}
                >
                  <div>{renderIcon(list.name, current.path === list.name)}</div>
                  {isOpenSidebar && (
                    <TextStyled isFocus={current.path === list.name} strong>
                      {list.title}
                    </TextStyled>
                  )}
                  {hasDot(list.name) && <MenuDot isOpen={isOpenSidebar} />}
                </ListStyled>
              );
            } else {
              const permissionSubMenu: any[] = [];
              const subMenu = roleData?.menus.find((x) => x.menuName === mapMenuPermission);
              list.subMenu.forEach((e: any, i) => {
                if ((subMenu?.permission[e.permission] || []).length > 0) {
                  permissionSubMenu.push(e);
                }
              });
              if (permissionSubMenu.length > 0) {
                return (
                  <CollapseMenu
                    isOpenSidebar={isOpenSidebar}
                    key={idx}
                    subLists={permissionSubMenu}
                    icon={renderIcon(list.name, current.path === list.name)}
                    name={list.name}
                    title={list.title}
                    setCurrent={setCurrent}
                    current={current}
                    frontPath={list.path}
                  />
                );
              }
            }
          }
        })}
      </div>
      <div
        style={{
          padding: "0 4px",
        }}
      >
        <Text level={7} fontWeight={700}>{` Dev : ${packageJson.version} `}</Text>
      </div>
    </MenuSiderStyled>
  );
}

export default MenuSider;
